import QorusClient from "@/api/clients/QorusClient";
import axios, { AxiosRequestConfig } from "axios";
import Uicookie from "@/utils/cookie";
import { ShoppingCart } from "@/models/shopping";
import { Address } from "@/models/address";

/**
 *  Qorus API about user's cart
 */
export default class CartService {

  private cancelSetOfferToken;
  private cancelSetShippingAddressToken;
  private cancelSetShippingMethodToken;
  private cancelGetShippingMethodToken;

  static of(): CartService {
    return new CartService();
  }

  getCustomerCart(userToken: string): Promise<ShoppingCart> {
    const axiosRequestConfig: AxiosRequestConfig = {
      headers: {
        "Customer-Authorization": userToken,
      },
    };
    return QorusClient.of().get("shopping-cart", axiosRequestConfig).then(
      (data: ShoppingCart) => {
        Uicookie.setCart(data.id);
        return ShoppingCart.of(data);
      },
      (data) => {
        Uicookie.deleteCart();
        throw data;
      },
    );
  }

  getAnonymousCart(shoppingCartId: string): Promise<ShoppingCart> {
    return QorusClient.of().get("shopping-cart/" + shoppingCartId).then(
      (data: ShoppingCart) => {
        Uicookie.setCart(data.id);
        return ShoppingCart.of(data);
      },
      (data) => {
        Uicookie.deleteCart();
        throw data;
      },
    );
  }

  removeCart(shoppingCartId: string): Promise<null> {
    return QorusClient.of().delete("shopping-cart/" + shoppingCartId).then(
      () => {
        Uicookie.deleteCart();
        return null;
      },
      (data) => {
        throw data;
      },
    );
  }

  setCoupons(coupon: string): Promise<ShoppingCart> {
    const cartId = Uicookie.getCart();
    const axiosRequestConfig: AxiosRequestConfig = {
      params: {
        coupon,
      },
    };
    return QorusClient.of().put("shopping-cart/" + cartId + "/set-coupon/", null, axiosRequestConfig).then(
      (data: ShoppingCart) => {
        return ShoppingCart.of(data);
      },
      (data) => {
        throw data;
      },
    );
  }

  removeCoupons(coupon: string): Promise<ShoppingCart> {
    const cartId = Uicookie.getCart();
    const axiosRequestConfig: AxiosRequestConfig = {
      params: {
        coupon,
      },
    };
    return QorusClient.of().delete("shopping-cart/" + cartId + "/remove-coupon/", axiosRequestConfig).then(
      (data: ShoppingCart) => {
        return ShoppingCart.of(data);
      },
      (data) => {
        throw data;
      },
    );
  }

  setOfferQuantity(params): Promise<ShoppingCart | null> {
    // method: string, offerId: string, addedQuantity: number, currentCart: ShoppingCart | null
    //v1/products?category=<category>
    //v1/products/categories/<category>
    // page = 0, pageSize = 10, sort = "price", asc = true;
    const userToken: string | null = Uicookie.getCustomerToken();

    let defaultQuantity = 0;
    let prevQty = 0;
    const cart: ShoppingCart | null = params.currentCart;
    if (cart && cart.cartItems && cart.cartItems.length) {
      const itemIsInCart = cart.cartItems.find(item => item.product?.offer?.id === params.offerId);
      prevQty = itemIsInCart?.quantity || 0;
    }
    switch (params.method) {
      case "add":
        defaultQuantity = params.quantity + prevQty;
        break;
      case "update" :
        defaultQuantity = params.quantity;
        break;
      case "remove":
        defaultQuantity = 0;
        break;
    }
    if (typeof this.cancelSetOfferToken != typeof undefined) {
      this.cancelSetOfferToken.cancel({ cancel: true });
    }

    this.cancelSetOfferToken = axios.CancelToken.source();

    const axiosRequestConfig: AxiosRequestConfig = {
      params: {
        offerId: params.offerId,
        quantity: Math.round(defaultQuantity),
        shoppingCartId: cart?.id,
      },
      cancelToken: this.cancelSetOfferToken.token,
    };

    return QorusClient.of().put("shopping-cart/set-offer-quantity/", null, axiosRequestConfig)
      .then(
        (data: ShoppingCart) => {

          if (data) {
            Uicookie.setCart(data.id);
            return ShoppingCart.of(data);
          } else {
            Uicookie.deleteCart();
            return null;
          }
        },
        (data) => {
          Uicookie.deleteCart();
          throw data;
        },
      );

  }

  setShippingAddress(shippingAddress: Address): Promise<ShoppingCart> | unknown {
    const userToken: string | null = Uicookie.getCustomerToken();
    if (userToken) {

      if (typeof this.cancelSetShippingAddressToken != typeof undefined) {
        this.cancelSetShippingAddressToken.cancel({ cancel: true });
      }
      if (typeof this.cancelSetShippingMethodToken != typeof undefined) {
        this.cancelSetShippingMethodToken.cancel({ cancel: true });
      }

      this.cancelSetShippingAddressToken = axios.CancelToken.source();

      const axiosRequestConfig: AxiosRequestConfig = {
        headers: {
          "Customer-Authorization": userToken,
        },
        cancelToken: this.cancelSetShippingAddressToken.token,
      };
      return QorusClient.of().put("shopping-cart/shipping-address", shippingAddress, axiosRequestConfig)
          .then(
              (data: ShoppingCart) => {
                return ShoppingCart.of(data);
              },
              (data) => {
                throw data;
              },
          );
    } else {
      Uicookie.error()
    }
  }

  setBillingAddress(billingAddressName): Promise<ShoppingCart> | unknown {
    const userToken: string | null = Uicookie.getCustomerToken();
    if (userToken) {
      const axiosRequestConfig: AxiosRequestConfig = {
        headers: {
          "Customer-Authorization": userToken,
        },
        params: {
          billingAddressName,
        },
      };
      return QorusClient.of().put("shopping-cart/billing-address", null, axiosRequestConfig)
          .then(
              (data: ShoppingCart) => {
                return ShoppingCart.of(data);
              },
              (data) => {
                throw data;
              },
          );
    } else {
      Uicookie.error()
    }
  }

  getShipmentMethods(): Promise<Array<unknown>> |unknown {
    const userToken: string | null = Uicookie.getCustomerToken();
    if (userToken) {

      if (typeof this.cancelGetShippingMethodToken != typeof undefined) {
        this.cancelGetShippingMethodToken.cancel({ cancel: true });
      }
      if (typeof this.cancelSetShippingMethodToken != typeof undefined) {
        this.cancelSetShippingMethodToken.cancel({ cancel: true });
      }

      this.cancelGetShippingMethodToken = axios.CancelToken.source();
      const axiosRequestConfig: AxiosRequestConfig = {
        headers: {
          "Customer-Authorization": userToken,
        },
        cancelToken: this.cancelGetShippingMethodToken.token,
      };
      return QorusClient.of().get("shopping-cart/shipment-methods", axiosRequestConfig)
          .then(
              (data) => {
                return data;
              },
              (data) => {
                throw data;
              },
          );
    } else {
      Uicookie.error()
    }
  }

  setShipmentMethods(selectedShipmentMethods): Promise<ShoppingCart> | unknown {
    const userToken: string | null = Uicookie.getCustomerToken();
    if (userToken) {

      if (typeof this.cancelSetShippingMethodToken != typeof undefined) {
        this.cancelSetShippingMethodToken.cancel({ cancel: true });
      }

      this.cancelSetShippingMethodToken = axios.CancelToken.source();

      const axiosRequestConfig: AxiosRequestConfig = {
        headers: {
          "Customer-Authorization": userToken,
        },
        cancelToken: this.cancelSetShippingMethodToken.token,
      };
      return QorusClient.of().put("shopping-cart/shipment-methods", { shipmentMethodIds: selectedShipmentMethods }, axiosRequestConfig)
          .then(
              (data: ShoppingCart) => {
                return ShoppingCart.of(data);
              },
              (data) => {
                throw data;
              },
          );
    } else {
      Uicookie.error()
    }
  }
}